import { useState } from 'react'
import { Button } from './ui'

interface FilterModalProps {
  isOpen: boolean
  onClose: () => void
  onApply: (filters: FilterState) => void
  initialFilters: FilterState
}

export interface FilterState {
  dateFrom: string
  dateTill: string
  dayOfWeek: {
    mon: boolean
    tue: boolean
    wed: boolean
    thu: boolean
    fri: boolean
    sat: boolean
    sun: boolean
  }
  postTypes: {
    posts: boolean
    reposts: boolean
    replays: boolean
  }
}

export default function FilterModal({ isOpen, onClose, onApply, initialFilters }: FilterModalProps) {
  const [filters, setFilters] = useState<FilterState>(initialFilters)
  const [expandedSections, setExpandedSections] = useState({
    postType: true,
    date: true,
    dayOfWeek: true,
  })

  if (!isOpen) return null

  const handleApply = () => {
    onApply(filters)
    onClose()
  }

  const handleReset = () => {
    const resetFilters: FilterState = {
      dateFrom: '',
      dateTill: '',
      dayOfWeek: {
        mon: true,
        tue: true,
        wed: true,
        thu: true,
        fri: true,
        sat: true,
        sun: true,
      },
      postTypes: {
        posts: true,
        reposts: true,
        replays: true,
      },
    }
    setFilters(resetFilters)
  }

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }))
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '2rem'
    }}>
      <div style={{
        backgroundColor: 'var(--base-000)',
        borderRadius: '1rem',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1.5rem',
          borderBottom: '1px solid var(--color-border)'
        }}>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: 'var(--color-text)'
          }}>
            Filter
          </h2>
          <button
            onClick={handleReset}
            style={{
              color: 'var(--color-primary)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '600'
            }}
          >
            Reset all
          </button>
        </div>

        {/* Scrollable Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem' }}>

          {/* Post Type Section */}
          <div style={{ marginBottom: '0.5rem', borderBottom: '1px solid var(--color-border)' }}>
            <button
              onClick={() => toggleSection('postType')}
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '1rem 0',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '1.125rem',
                fontWeight: '600',
                color: 'var(--color-text)'
              }}
            >
              Skeet type
              <svg 
                width="20" 
                height="20" 
                fill="currentColor" 
                viewBox="0 0 24 24"
                style={{ 
                  transform: expandedSections.postType ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s'
                }}
              >
                <path d="M7 10l5 5 5-5z"/>
              </svg>
            </button>
            {expandedSections.postType && (
              <div style={{ paddingBottom: '1rem' }}>
                {[
                  { key: 'posts', label: 'Your skeets' },
                  { key: 'reposts', label: 'Reposts' },
                  { key: 'replays', label: 'Replies' }
                ].map(({ key, label }) => (
                  <label key={key} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.75rem 0',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={filters.postTypes[key as keyof typeof filters.postTypes]}
                      onChange={(e) => setFilters({
                        ...filters,
                        postTypes: { ...filters.postTypes, [key]: e.target.checked }
                      })}
                      style={{ 
                        width: '20px', 
                        height: '20px',
                        cursor: 'pointer',
                        accentColor: 'var(--color-primary)'
                      }}
                    />
                    <span style={{ fontSize: '1rem', color: 'var(--color-text)' }}>{label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Date Section */}
          <div style={{ marginBottom: '0.5rem', borderBottom: '1px solid var(--color-border)' }}>
            <button
              onClick={() => toggleSection('date')}
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '1rem 0',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '1.125rem',
                fontWeight: '600',
                color: 'var(--color-text)'
              }}
            >
              Date
              <svg 
                width="20" 
                height="20" 
                fill="currentColor" 
                viewBox="0 0 24 24"
                style={{ 
                  transform: expandedSections.date ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s'
                }}
              >
                <path d="M7 10l5 5 5-5z"/>
              </svg>
            </button>
            {expandedSections.date && (
              <div style={{ paddingBottom: '1rem' }}>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    marginBottom: '0.5rem',
                    color: 'var(--color-text)',
                    fontWeight: '600'
                  }}>
                    From
                  </label>
                  <input
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid var(--color-border)',
                      borderRadius: '0.5rem',
                      fontSize: '1rem',
                      outline: 'none'
                    }}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    marginBottom: '0.5rem',
                    color: 'var(--color-text)',
                    fontWeight: '600'
                  }}>
                    To
                  </label>
                  <input
                    type="date"
                    value={filters.dateTill}
                    onChange={(e) => setFilters({ ...filters, dateTill: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid var(--color-border)',
                      borderRadius: '0.5rem',
                      fontSize: '1rem',
                      outline: 'none'
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Day of Week Section */}
          <div style={{ marginBottom: '0.5rem' }}>
            <button
              onClick={() => toggleSection('dayOfWeek')}
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '1rem 0',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '1.125rem',
                fontWeight: '600',
                color: 'var(--color-text)'
              }}
            >
              Day of the week
              <svg 
                width="20" 
                height="20" 
                fill="currentColor" 
                viewBox="0 0 24 24"
                style={{ 
                  transform: expandedSections.dayOfWeek ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s'
                }}
              >
                <path d="M7 10l5 5 5-5z"/>
              </svg>
            </button>
            {expandedSections.dayOfWeek && (
              <div style={{ paddingBottom: '1rem' }}>
                {Object.entries(filters.dayOfWeek).map(([day, checked]) => (
                  <label key={day} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.75rem 0',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => setFilters({
                        ...filters,
                        dayOfWeek: { ...filters.dayOfWeek, [day]: e.target.checked }
                      })}
                      style={{ 
                        width: '20px', 
                        height: '20px',
                        cursor: 'pointer',
                        accentColor: 'var(--color-primary)'
                      }}
                    />
                    <span style={{ fontSize: '1rem', color: 'var(--color-text)' }}>
                      {day.charAt(0).toUpperCase() + day.slice(1)}day
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div style={{
          padding: '1.5rem',
          borderTop: '1px solid var(--color-border)',
          display: 'flex',
          gap: '1rem'
        }}>
          <Button 
            variant="secondary" 
            onClick={onClose} 
            style={{ 
              flex: 1,
              borderRadius: '2rem',
              padding: '0.75rem 2rem'
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleApply} 
            style={{ 
              flex: 1,
              backgroundColor: 'var(--color-text)',
              color: 'white',
              borderRadius: '2rem',
              padding: '0.75rem 2rem'
            }}
          >
            Apply
          </Button>
        </div>
      </div>
    </div>
  )
}

